import React, { useState, useRef } from 'react';
import { useApp } from '../contexts/AppContext';
import { Download, AlertCircle, RefreshCw, ShieldOff } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/* ─── helpers ────────────────────────────────────────────────── */
const g = (obj, ...keys) => {
  if (!obj) return '';
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '' && v !== 'null') return String(v);
  }
  return '';
};

const fmt = (v) => (v && v !== '0' && v !== '0.0' ? v : '—');
const fmtAmt = (v) => {
  const s = String(v || '');
  return s && s !== '0' && s !== '0.0' ? `₹${s}` : '—';
};

/* ─── Score extraction (comprehensive) ──────────────────────── */
function extractScore(scores, b2c, oldCr) {
  // 1. Try SCORE array — first pass: standard range 300-900
  for (const s of scores) {
    const candidates = ['VALUE','SCORE-VALUE','BCC-SCORE','SCORE','SCORE-RATING','CREDIT-SCORE','BUREAU-SCORE'];
    for (const k of candidates) {
      const v = parseInt(s[k]);
      if (!isNaN(v) && v >= 300 && v <= 900) return { value: v, obj: s };
    }
    for (const [k, raw] of Object.entries(s || {})) {
      if (typeof raw !== 'string' && typeof raw !== 'number') continue;
      const v = parseInt(raw);
      if (!isNaN(v) && v >= 300 && v <= 900 && !k.includes('DATE') && !k.includes('TERM'))
        return { value: v, obj: s };
    }
  }
  // 2. Try SCORE array again — no range restriction (catches scores like 16)
  for (const s of scores) {
    const candidates = ['VALUE','SCORE-VALUE','BCC-SCORE','SCORE','CREDIT-SCORE','BUREAU-SCORE'];
    for (const k of candidates) {
      const v = parseInt(s[k]);
      if (!isNaN(v) && v > 0) return { value: v, obj: s };
    }
  }
  // 3. Fall back to TRENDS most recent value (IDS Pay shows trend[0] when SCORE is absent)
  const trends = b2c?.['REPORT-DATA']?.['TRENDS'] || {};
  if (trends['NAME'] && trends['VALUES']) {
    const firstVal = trends['VALUES'].split('|')[0];
    const tv = parseInt(firstVal);
    if (!isNaN(tv) && tv > 0) return { value: tv, obj: trends, fromTrend: true };
  }
  // 4. REQUESTED-SERVICES
  const reqSvc = b2c?.['REPORT-DATA']?.['REQUESTED-SERVICES'] || [];
  for (const svc of reqSvc) {
    const sr = svc?.['SERVICE-RESPONSE'] || {};
    for (const [, raw] of Object.entries(sr)) {
      const v = parseInt(raw);
      if (!isNaN(v) && v >= 300 && v <= 900) return { value: v, obj: sr };
    }
  }
  // 5. Old/mock format
  const sv = parseInt(g(oldCr?.scores, 'score_value'));
  if (!isNaN(sv) && sv > 0) return { value: sv, obj: oldCr.scores, oldFormat: true };
  return null;
}

/* ─── DEMOGS variations extraction (handles nested VARIATION) ── */
function extractVariations(std) {
  const demogs = std?.['DEMOGS'] || {};
  const raw    = demogs['VARIATIONS'];
  if (!raw) return {};
  const result  = {};
  const addItem = (type, val, date) => {
    if (!val) return;
    const t = type.replace(/-VARIATIONS?$/i, '').trim().toUpperCase();
    if (!result[t]) result[t] = [];
    result[t].push([val, date || '—']);
  };
  if (Array.isArray(raw)) {
    raw.forEach(outerItem => {
      const type     = g(outerItem, 'TYPE', 'VARIATION-TYPE', 'type') || 'VARIATION';
      const innerArr = outerItem['VARIATION'] || outerItem['VARIATIONS'] || [];
      if (Array.isArray(innerArr) && innerArr.length > 0) {
        innerArr.forEach(inner => {
          addItem(type,
            g(inner, 'VALUE', 'value', 'VARIATION-VALUE'),
            g(inner, 'REPORTED-DT', 'DATE-REPORTED', 'REPORTED-DATE'));
        });
      } else {
        addItem(type,
          g(outerItem, 'VALUE', 'value', 'VARIATION-VALUE'),
          g(outerItem, 'REPORTED-DT', 'DATE-REPORTED', 'date'));
      }
    });
  } else if (typeof raw === 'object') {
    Object.entries(raw).forEach(([type, items]) => {
      if (!Array.isArray(items)) return;
      items.forEach(item => addItem(type, g(item, 'VALUE', 'value'),
        g(item, 'REPORTED-DT', 'DATE-REPORTED')));
    });
  }
  return result;
}

/* ─── Parse ALL history types from HISTORY array ─────────── */
function parseAllHistory(tl) {
  const histArr = tl?.['HISTORY'] || [];
  const result  = {};
  histArr.forEach(h => {
    const name   = h['NAME'] || 'UNKNOWN';
    const dates  = (h['DATES']  || '').split('|').filter(Boolean);
    const values = (h['VALUES'] || '').split('|').filter(Boolean);
    if (!dates.length) return;
    const byYear = {};
    dates.forEach((d, i) => {
      const [mon, yr] = d.trim().split(':');
      if (!yr || !mon) return;
      if (!byYear[yr]) byYear[yr] = {};
      byYear[yr][mon] = (values[i] || '').trim();
    });
    if (Object.keys(byYear).length) result[name] = byYear;
  });
  return result;
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/* ─── Score Gauge ────────────────────────────────────────────── */
const ScoreGauge = ({ score }) => {
  const n = parseInt(score) || 0;
  const r = 45; const c = 2 * Math.PI * r;
  const offset = c - (Math.min(n, 900) / 900) * c;
  const color  = n >= 750 ? '#10B981' : n >= 650 ? '#F59E0B' : '#EF4444';
  const label  = n >= 750 ? 'Excellent' : n >= 700 ? 'Good' : n >= 650 ? 'Fair' : 'Poor';
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="90" height="90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="currentColor" strokeWidth="8" className="text-border" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={c} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 50 50)"
          style={{ transition: 'stroke-dashoffset 1.5s ease-out' }} />
        <text x="50" y="46" textAnchor="middle" fontSize="18" fontWeight="bold" fill={color}>{n}</text>
        <text x="50" y="60" textAnchor="middle" fontSize="8" fill={color}>{label}</text>
      </svg>
      <span className="text-xs text-muted-foreground">out of 900</span>
    </div>
  );
};

/* ─── Section header ─────────────────────────────────────────── */
const SH = ({ title }) => (
  <div className="bg-[#00476F] text-white text-xs font-bold px-3 py-1.5 mt-4 first:mt-0">
    {title}
  </div>
);

/* ─── Summary table row ──────────────────────────────────────── */
const SumTable = ({ head, row }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr className="bg-[#d9e8f5]">
          {head.map((h, i) => (
            <th key={i} className="border border-[#aac8df] px-1.5 py-1 text-center font-semibold text-[10px] leading-tight whitespace-nowrap">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {row.map((v, i) => (
            <td key={i} className="border border-[#aac8df] px-1.5 py-1 text-center text-[11px]">
              {v || '—'}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  </div>
);

/* ─── Account field pair ─────────────────────────────────────── */
const FieldPair = ({ label, value }) =>
  value && value !== '—' ? (
    <div className="flex gap-1 py-0.5 border-b border-gray-100 last:border-0">
      <span className="text-[10px] text-[#00476F] font-semibold w-36 shrink-0">{label}</span>
      <span className="text-[11px] text-gray-800 break-all">{value}</span>
    </div>
  ) : null;

/* ─── Credit Report Template ─────────────────────────────────── */
const CreditReportTemplate = ({ b2cReport, oldCr, isMock, pdfFooterNote, user }) => {
  const hdr    = b2cReport?.['HEADER-SEGMENT'] || {};
  const app    = b2cReport?.['REQUEST-DATA']?.['APPLICANT-SEGMENT'] || {};
  const std    = b2cReport?.['REPORT-DATA']?.['STANDARD-DATA'] || {};
  const accSum = b2cReport?.['REPORT-DATA']?.['ACCOUNTS-SUMMARY'] || {};
  const priSum = accSum['PRIMARY-ACCOUNTS-SUMMARY']   || {};
  const secSum = accSum['SECONDARY-ACCOUNTS-SUMMARY'] || {};
  const mfiSum = accSum['MFI-GROUP-ACCOUNTS-SUMMARY'] || {};
  const perfAttrs = accSum['PERFORM-ATTRIBUTES'] || [];

  const scoreInfo  = extractScore(std['SCORE'] || [], b2cReport, oldCr);
  const scoreVal   = scoreInfo?.value || 0;
  const scoreObj   = scoreInfo?.obj || {};
  const scoreName  = scoreInfo?.oldFormat
    ? g(oldCr?.scores, 'score_type')
    : (g(scoreObj, 'NAME', 'SCORE-TYPE', 'SCORE-NAME') || 'PERFORM CONSUMER 2.2');
  const scoreFactorsRaw = scoreObj['FACTORS'];
  const scoreFactors = Array.isArray(scoreFactorsRaw)
    ? scoreFactorsRaw.map(f => f['TYPE'] && f['DESC'] ? `${f['TYPE']}: ${f['DESC']}` : (f['TYPE'] || f['DESC'] || '')).filter(Boolean).join('  ·  ')
    : g(scoreObj, 'SCORE-FACTORS', 'FACTOR-CODES', 'FACTORS');

  // Score trend from REPORT-DATA.TRENDS
  const trends      = b2cReport?.['REPORT-DATA']?.['TRENDS'] || {};
  const trendDates  = (trends['DATES']  || '').split('|').filter(Boolean);
  const trendValues = (trends['VALUES'] || '').split('|').filter(Boolean);
  const trendName   = trends['RESERVED1'] || 'PERFORM CONSUMER V2.2';
  const sColor = scoreVal >= 750 ? '#10B981' : scoreVal >= 650 ? '#F59E0B' : '#EF4444';

  const appName  = [app['FIRST-NAME'], app['MIDDLE-NAME'], app['LAST-NAME']].filter(Boolean).join(' ').trim();
  const appPhone = app['PHONES']?.[0]?.['VALUE'] || '';
  const tradelines = std['TRADELINES'] || [];
  const inquiries  = std['INQUIRY-HISTORY'] || [];
  const rawEmp     = std['EMPLOYMENT-DETAILS'] || [];
  const employment = rawEmp.map(e => e['EMPLOYMENT-DETAIL'] || e).filter(Boolean);
  const varGroups  = extractVariations(std);
  const varKeys    = Object.keys(varGroups);

  const getAttr = (name) => {
    const a = perfAttrs.find(x => x['ATTR-NAME'] === name);
    return a ? String(a['ATTR-VALUE'] ?? '—') : '—';
  };

  return (
    <div className="bg-white text-gray-800 font-[Arial,sans-serif] text-xs print:text-[8pt]" id="crif-report-template">
      {/* Demo banner */}
      {isMock && (
        <div className="bg-amber-50 border border-amber-300 px-3 py-1.5 text-amber-700 text-xs font-medium mb-2 rounded">
          ⚠ Demo Mode — Sample data
        </div>
      )}

      {/* ── Page 1: Header ────────────────────────────── */}
      <div className="bg-[#00476F] text-white px-4 py-2 flex items-center justify-between">
        <div>
          <p className="font-bold text-base">CONSUMER CREDIT™ REPORT</p>
          <p className="text-[10px] opacity-80">CRIF High Mark Credit Information Services Pvt. Ltd.</p>
        </div>
        <div className="text-right text-[10px]">
          <p>CHM Ref #: {g(hdr, 'REPORT-ID') || '—'}</p>
          <p>Date of Request: {g(hdr, 'DATE-OF-REQUEST') || '—'}</p>
          <p>Date of Issue: {g(hdr, 'DATE-OF-ISSUE') || '—'}</p>
        </div>
      </div>

      {/* Inquiry Input */}
      <div className="bg-[#e8f4ff] border border-[#aac8df] px-4 py-2 flex gap-6 flex-wrap text-[11px]">
        <span><strong className="text-[#00476F]">Name:</strong> {appName || '—'}</span>
        <span><strong className="text-[#00476F]">Phone:</strong> {appPhone || '—'}</span>
        {g(app, 'GENDER') && false && <span><strong className="text-[#00476F]">Gender:</strong> {g(app, 'GENDER')}</span>}
        {g(app, 'DOB') && <span><strong className="text-[#00476F]">DOB:</strong> {g(app, 'DOB')}</span>}
        <span><strong className="text-[#00476F]">Product:</strong> {g(hdr, 'PRODUCT-TYPE') || '—'}</span>
      </div>

      {/* Applicant IDs / Address / Email — shown when present */}
      {(() => {
        const ids   = Array.isArray(app['IDS'])       ? app['IDS'].filter(x => g(x,'VALUE','ID-VALUE','ID-NUMBER'))      : [];
        const addrs = Array.isArray(app['ADDRESSES'])  ? app['ADDRESSES'].filter(x => g(x,'VALUE','ADDRESS','FULL-ADDRESS')) : [];
        const emls  = Array.isArray(app['EMAILS'])     ? app['EMAILS'].filter(x => g(x,'VALUE','EMAIL-ID','EMAIL'))         : [];
        if (!ids.length && !addrs.length && !emls.length) return null;
        return (
          <div className="bg-[#f8fbff] border-b border-[#aac8df] px-4 py-1.5 text-[10px] space-y-0.5">
            {ids.length > 0 && (
              <div><strong className="text-[#00476F]">Identity Docs: </strong>
                {ids.map((id,i) => <span key={i} className="mr-3">{g(id,'TYPE','ID-TYPE')}: {g(id,'VALUE','ID-VALUE','ID-NUMBER')}</span>)}
              </div>
            )}
            {emls.length > 0 && (
              <div><strong className="text-[#00476F]">Email: </strong>
                {emls.map((e,i) => <span key={i} className="mr-3">{g(e,'VALUE','EMAIL-ID','EMAIL')}</span>)}
              </div>
            )}
            {addrs.length > 0 && (
              <div><strong className="text-[#00476F]">Address: </strong>
                {addrs.slice(0,2).map((a,i) => <span key={i} className="mr-3">{g(a,'VALUE','ADDRESS','FULL-ADDRESS')}</span>)}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Credit Score ──────────────────────────────── */}
      <SH title="CRIF Credit Score" />
      <div className="flex gap-4 px-3 py-3">
        <ScoreGauge score={scoreVal} />
        <div className="flex-1">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-[#d9e8f5]">
                {['SCORE NAME','RANGE','SCORE','SCORE FACTORS'].map((h,i) => (
                  <th key={i} className="border border-[#aac8df] px-2 py-1 text-center font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-[#aac8df] px-2 py-1">{scoreName}</td>
                <td className="border border-[#aac8df] px-2 py-1 text-center">300 - 900</td>
                <td className="border border-[#aac8df] px-2 py-2 text-center">
                  <span style={{ color: sColor }} className="text-2xl font-extrabold">{scoreVal || 'N/A'}</span>
                </td>
                <td className="border border-[#aac8df] px-2 py-1 text-[10px] leading-relaxed">{scoreFactors || '—'}</td>
              </tr>
            </tbody>
          </table>
          {perfAttrs.length > 0 && (() => {
            const creditHistYr = getAttr('LENGTH-OF-CREDIT-HISTORY-YEAR');
            const creditHistMo = getAttr('LENGTH-OF-CREDIT-HISTORY-MONTH');
            const avgAgeYr     = getAttr('AVERAGE-ACCOUNT-AGE-YEAR');
            const avgAgeMo     = getAttr('AVERAGE-ACCOUNT-AGE-MONTH');
            const attrs = [
              { label:'Inquiries (6mo)',   val: getAttr('INQUIRIES-IN-LAST-SIX-MONTHS') },
              { label:'Credit History',    val: creditHistMo !== '—' ? `${creditHistYr}y ${creditHistMo}m` : `${creditHistYr}y` },
              { label:'Avg Account Age',   val: avgAgeMo !== '—' ? `${avgAgeYr}y ${avgAgeMo}m` : `${avgAgeYr}y` },
              { label:'New Accts (6mo)',   val: getAttr('NEW-ACCOUNTS-IN-LAST-SIX-MONTHS') },
              { label:'New Delinq (6mo)', val: getAttr('NEW-DELINQ-ACCOUNT-IN-LAST-SIX-MONTHS') },
            ];
            return (
              <div className="mt-2">
                <table className="w-full text-[10px] border-collapse">
                  <thead><tr className="bg-[#d9e8f5]">
                    {attrs.map((a,i) => <th key={i} className="border border-[#aac8df] px-1 py-0.5 text-center">{a.label}</th>)}
                  </tr></thead>
                  <tbody><tr>
                    {attrs.map((a,i) => <td key={i} className="border border-[#aac8df] px-1 py-0.5 text-center">{a.val}</td>)}
                  </tr></tbody>
                </table>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Score Trend (historical scores from REPORT-DATA.TRENDS) */}
      {trendDates.length > 0 && (
        <div className="px-3 pb-3">
          <p className="text-[10px] font-bold text-[#00476F] mb-1">Score Trend — {trendName}</p>
          <div className="overflow-x-auto">
            <table className="text-[9px] border-collapse">
              <thead>
                <tr className="bg-[#d9e8f5]">
                  <th className="border border-[#aac8df] px-2 py-0.5 font-bold text-center bg-[#c0d8ec]">RETRO DATE</th>
                  {trendDates.map((d, i) => (
                    <th key={i} className="border border-[#aac8df] px-1.5 py-0.5 text-center whitespace-nowrap font-semibold">{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white">
                  <td className="border border-[#aac8df] px-2 py-0.5 font-bold text-center bg-[#e8f4ff]">SCORE</td>
                  {trendValues.map((v, i) => {
                    const n = parseInt(v);
                    const col = n >= 750 ? '#15803d' : n >= 650 ? '#b45309' : '#dc2626';
                    return (
                      <td key={i} className="border border-[#aac8df] px-1.5 py-0.5 text-center font-bold" style={{color: col}}>{v}</td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── All Performance Attributes ─────────────────────── */}
      {perfAttrs.length > 0 && (() => {
        const nonZero = perfAttrs.filter(a => a['ATTR-VALUE'] && a['ATTR-VALUE'] !== '0' && a['ATTR-VALUE'] !== '');
        if (!nonZero.length) return null;
        return (
          <>
            <SH title="PERFORM ATTRIBUTES" />
            <div className="px-2 py-1">
              <div className="grid grid-cols-2 gap-x-6 gap-y-0">
                {nonZero.map((attr, i) => (
                  <div key={i} className="flex gap-1.5 text-[9px] border-b border-gray-100 py-0.5">
                    <span className="text-[#00476F] font-semibold shrink-0 w-52 leading-tight">
                      {attr['ATTR-NAME'].replace(/-/g, ' ')}:
                    </span>
                    <span className="text-gray-800 font-medium">{attr['ATTR-VALUE']}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        );
      })()}

      {/* ── Account Summaries ─────────────────────────── */}
      <SH title="PRIMARY ACCOUNTS SUMMARY" />
      <div className="px-2 py-1">
        <SumTable
          head={['NO. ACCTS','ACTIVE','OVERDUE','SECURED','UNSECURED','UNTAGGED','TOT CURR BAL','BAL SECD','BAL UNSECD','SANCT AMT','DISB AMT','OVRD AMT']}
          row={[
            g(priSum,'NUMBER-OF-ACCOUNTS'), g(priSum,'ACTIVE-ACCOUNTS'), g(priSum,'OVERDUE-ACCOUNTS'),
            g(priSum,'SECURED-ACCOUNTS'), g(priSum,'UNSECURED-ACCOUNTS'), g(priSum,'UNTAGGED-ACCOUNTS'),
            fmtAmt(g(priSum,'TOTAL-CURRENT-BALANCE')), fmtAmt(g(priSum,'CURRENT-BALANCE-SECURED')),
            fmtAmt(g(priSum,'CURRENT-BALANCE-UNSECURED')), fmtAmt(g(priSum,'TOTAL-SANCTIONED-AMT')),
            fmtAmt(g(priSum,'TOTAL-DISBURSED-AMT')), fmtAmt(g(priSum,'TOTAL-AMT-OVERDUE')),
          ]}
        />
      </div>

      <SH title="SECONDARY ACCOUNTS SUMMARY" />
      <div className="px-2 py-1">
        <SumTable
          head={['NO. ACCTS','ACTIVE','OVERDUE','SECURED','UNSECURED','UNTAGGED','TOT CURR BAL','SANCT AMT','DISB AMT','OVRD AMT']}
          row={[
            g(secSum,'NUMBER-OF-ACCOUNTS'), g(secSum,'ACTIVE-ACCOUNTS'), g(secSum,'OVERDUE-ACCOUNTS'),
            g(secSum,'SECURED-ACCOUNTS'), g(secSum,'UNSECURED-ACCOUNTS'), g(secSum,'UNTAGGED-ACCOUNTS'),
            fmtAmt(g(secSum,'TOTAL-CURRENT-BALANCE')), fmtAmt(g(secSum,'TOTAL-SANCTIONED-AMT')),
            fmtAmt(g(secSum,'TOTAL-DISBURSED-AMT')), fmtAmt(g(secSum,'TOTAL-AMT-OVERDUE')),
          ]}
        />
      </div>

      <SH title="MFI GROUP ACCOUNTS SUMMARY" />
      <div className="px-2 py-1">
        <SumTable
          head={['NO. ACCTS','ACTIVE','OVERDUE','CLOSED','OTH MFIS','OWN MFIS','OWN CURR BAL','OWN DISB AMT','OTH CURR BAL','OTH DISB AMT','MAX DELINQ']}
          row={[
            g(mfiSum,'NUMBER-OF-ACCOUNTS'), g(mfiSum,'ACTIVE-ACCOUNTS'), g(mfiSum,'OVERDUE-ACCOUNTS'),
            g(mfiSum,'CLOSED-ACCOUNTS'), g(mfiSum,'NO-OF-OTHER-MFIS'), g(mfiSum,'NO-OF-OWN-MFIS'),
            fmtAmt(g(mfiSum,'TOTAL-OWN-CURRENT-BALANCE')), fmtAmt(g(mfiSum,'TOTAL-OWN-DISBURSED-AMT')),
            fmtAmt(g(mfiSum,'TOTAL-OTHER-CURRENT-BALANCE')), fmtAmt(g(mfiSum,'TOTAL-OTHER-DISBURSED-AMT')),
            g(mfiSum,'MAX-WORST-DELINQUENCY'),
          ]}
        />
      </div>

      {/* ── Additional Grantor Summary ─────────────────── */}
      {(() => {
        const addSum = b2cReport?.['REPORT-DATA']?.['ACCOUNTS-SUMMARY']?.['ADDITIONAL-SUMMARY'];
        const items = Array.isArray(addSum)
          ? addSum.filter(a => a['ATTR-VALUE'] && a['ATTR-VALUE'] !== '0' && a['ATTR-VALUE'] !== '')
          : [];
        if (!items.length) return null;
        return (
          <>
            <SH title="ADDITIONAL GRANTOR SUMMARY" />
            <div className="px-2 py-1">
              <SumTable
                head={items.map(a => a['ATTR-NAME'].replace(/-/g, ' '))}
                row={items.map(a => a['ATTR-VALUE'])}
              />
            </div>
          </>
        );
      })()}

      {/* ── Personal Info Variations ──────────────────── */}
      {varKeys.length > 0 && (
        <>
          <SH title="PERSONAL INFORMATION VARIATIONS" />
          <div className="px-2 py-1 space-y-2">
            {varKeys.map(type => (
              <div key={type}>
                <p className="text-[10px] font-bold text-[#00476F] mb-0.5">{type} VARIATIONS</p>
                <table className="w-full text-[10px] border-collapse">
                  <thead>
                    <tr className="bg-[#e8f4ff]">
                      <th className="border border-[#aac8df] px-2 py-0.5 text-left">Value</th>
                      <th className="border border-[#aac8df] px-2 py-0.5 text-center w-28">Reported Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {varGroups[type].map(([val, date], i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f5f9ff]'}>
                        <td className="border border-[#aac8df] px-2 py-0.5">{val}</td>
                        <td className="border border-[#aac8df] px-2 py-0.5 text-center">{date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Employment ────────────────────────────────── */}
      {employment.length > 0 && (
        <>
          <SH title="EMPLOYMENT DETAILS" />
          <div className="px-2 py-1">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-[#d9e8f5]">
                  {['Occupation','Account Type','First Reported','Last Reported','Source'].map((h,i) => (
                    <th key={i} className="border border-[#aac8df] px-2 py-0.5 text-center">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employment.map((emp, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f5f9ff]'}>
                    <td className="border border-[#aac8df] px-2 py-0.5">{g(emp,'OCCUPATION','occupation') || '—'}</td>
                    <td className="border border-[#aac8df] px-2 py-0.5">{g(emp,'ACCT-TYPE','ACCOUNT-TYPE') || '—'}</td>
                    <td className="border border-[#aac8df] px-2 py-0.5 text-center">{g(emp,'FIRST-REPORTED-DT','DATE-REPORTED') || '—'}</td>
                    <td className="border border-[#aac8df] px-2 py-0.5 text-center">{g(emp,'LAST-REPORTED-DT','REPORTED-DATE') || '—'}</td>
                    <td className="border border-[#aac8df] px-2 py-0.5 text-center">{g(emp,'SOURCE-INDICATOR') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Account List ──────────────────────────────── */}
      {tradelines.length > 0 && (
        <>
          <SH title={`ACCOUNT INFORMATION (${tradelines.length} accounts)`} />
          <div className="px-2 py-1 overflow-x-auto">
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="bg-[#d9e8f5]">
                  {['#','Account Type','Credit Grantor','Account Number','Status','Reported Date'].map((h,i)=>(
                    <th key={i} className="border border-[#aac8df] px-1.5 py-0.5 text-center font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tradelines.map((tl, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f5f9ff]'}>
                    <td className="border border-[#aac8df] px-1.5 py-0.5 text-center">{i+1}</td>
                    <td className="border border-[#aac8df] px-1.5 py-0.5">{g(tl,'ACCT-TYPE','ACCOUNT-TYPE')}</td>
                    <td className="border border-[#aac8df] px-1.5 py-0.5 font-medium">{g(tl,'CREDIT-GRANTOR','SUBSCRIBER-NAME')}</td>
                    <td className="border border-[#aac8df] px-1.5 py-0.5 font-mono text-[9px]">{g(tl,'ACCT-NUMBER','ACCOUNT-NUMBER')}</td>
                    <td className="border border-[#aac8df] px-1.5 py-0.5">
                      <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${(() => {
                        const s = (g(tl,'ACCOUNT-STATUS') || '').toLowerCase();
                        const ov = g(tl,'OVERDUE-AMT','AMOUNT-OVERDUE');
                        const hasOv = ov && ov !== '0' && ov !== '0.0';
                        if (hasOv) return 'bg-red-100 text-red-700';
                        if (s === 'active') return 'bg-emerald-100 text-emerald-700';
                        return 'bg-blue-100 text-blue-700';
                      })()}`}>
                        {g(tl,'ACCOUNT-STATUS') || '—'}
                      </span>
                    </td>
                    <td className="border border-[#aac8df] px-1.5 py-0.5 text-center">{g(tl,'REPORTED-DT','DATE-REPORTED')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Per-account detail sections */}
          {tradelines.map((tl, idx) => {
            const lender   = g(tl,'CREDIT-GRANTOR','SUBSCRIBER-NAME');
            const acctType = g(tl,'ACCT-TYPE','ACCOUNT-TYPE');
            const allHistory  = parseAllHistory(tl);
            const histTypeNames = Object.keys(allHistory);
            const hasOverdue = (() => { const ov = g(tl,'OVERDUE-AMT','AMOUNT-OVERDUE'); return ov && ov !== '0' && ov !== '0.0'; })();
            const statusBg   = hasOverdue ? '#b91c1c' : (g(tl,'ACCOUNT-STATUS') === 'Active' ? '#166534' : '#1e40af');
            const fields1  = [
              ['Acct Number',         g(tl,'ACCT-NUMBER','ACCOUNT-NUMBER')],
              ['Credit Grantor',      lender],
              ['Credit Grantor Type', g(tl,'CREDIT-GRANTOR-TYPE','SUBSCRIBER-TYPE')],
              ['Account Type',        acctType],
              ['Account Status',      g(tl,'ACCOUNT-STATUS')],
              ['Ownership Type',      g(tl,'OWNERSHIP-TYPE','OWNERSHIP-IND')],
              ['Disbursed Amount',    fmtAmt(g(tl,'DISBURSED-AMT'))],
              ['Disbursed Date',      g(tl,'DISBURSED-DT','DATE-DISBURSED')],
              ['Current Balance',     fmtAmt(g(tl,'CURRENT-BAL','CURRENT-BALANCE'))],
              ['Overdue Amount',      `₹${g(tl,'OVERDUE-AMT','AMOUNT-OVERDUE') || '0'}`],
              ['Installment Amt',     g(tl,'INSTALLMENT-AMT') ? `₹${g(tl,'INSTALLMENT-AMT')}` : '—'],
              ['Inst. Frequency',     g(tl,'INSTALLMENT-FREQUENCY')],
            ].filter(([,v]) => v && v !== '—');
            const fields2  = [
              ['Credit Grantor Group',g(tl,'CREDIT-GRANTOR-GROUP')],
              ['Credit Limit',        fmtAmt(g(tl,'CREDIT-LIMIT'))],
              ['Cash Limit',          fmtAmt(g(tl,'CASH-LIMIT'))],
              ['Interest Rate',       g(tl,'INTEREST-RATE') ? `${g(tl,'INTEREST-RATE')}%` : '—'],
              ['Repayment Tenure',    g(tl,'REPAYMENT-TENURE') ? `${g(tl,'REPAYMENT-TENURE')} months` : ''],
              ['Reported Date',       g(tl,'REPORTED-DT','DATE-REPORTED')],
              ['Closed Date',         g(tl,'CLOSED-DT','CLOSED-DATE')],
              ['Last Payment Date',   g(tl,'LAST-PAYMENT-DT','LAST-PAYMENT-DATE')],
              ['Last Paid Amount',    fmtAmt(g(tl,'LAST-PAID-AMOUNT'))],
              ['Security Status',     g(tl,'SECURITY-STATUS')],
              ['Write Off Amt',       fmtAmt(g(tl,'WRITE-OFF-AMT'))],
              ['Write Off Date',      g(tl,'WRITE-OFF-DT')],
              ['Settlement Amt',      fmtAmt(g(tl,'SETTLEMENT-AMT'))],
              ['Written Off Status',  g(tl,'WRITTEN-OFF-SETTLED-STATUS')],
              ['Account Remarks',     g(tl,'ACCOUNT-REMARKS')],
              ['Income Amount',       fmtAmt(g(tl,'INCOME-AMOUNT'))],
              ['Income Frequency',    g(tl,'INCOME-FREQUENCY')],
              ['Occupation',          g(tl,'OCCUPATION')],
              ['Actual Payment',      g(tl,'ACTUAL-PAYMENT') ? `₹${g(tl,'ACTUAL-PAYMENT')}` : ''],
              ['Original Term',       g(tl,'ORIGINAL-TERM')],
              ['Term to Maturity',    g(tl,'TERM-TO-MATURITY')],
              ['Principal Write Off', fmtAmt(g(tl,'PRINCIPAL-WRITE-OFF-AMT'))],
              ['Suit Filed Status',   g(tl,'SUIT-FILED-WILFUL-DEFAULT-STATUS')],
              ['Suit Filed Date',     g(tl,'SUIT-FILED-DT')],
              ['Acct in Dispute',     g(tl,'ACCT-IN-DISPUTE')],
              ['Security Details',    (() => {
                const sd = tl['SECURITY-DETAILS'];
                if (!Array.isArray(sd) || !sd.length) return '';
                return sd.map(s => {
                  const parts = [];
                  if (s['SECURITY-TYPE'])       parts.push(`Type: ${s['SECURITY-TYPE']}`);
                  if (s['SECURITY-VALUATION'] && s['SECURITY-VALUATION'] !== '0') parts.push(`Val: ₹${s['SECURITY-VALUATION']}`);
                  if (s['SECURITY-CHARGE'])     parts.push(`Charge: ${s['SECURITY-CHARGE']}`);
                  if (s['OWNER-NAME'])          parts.push(`Owner: ${s['OWNER-NAME']}`);
                  if (s['DATE-OF-VALUATION'])   parts.push(`Val Date: ${s['DATE-OF-VALUATION']}`);
                  if (s['PROPERTY-ADDRESS'])    parts.push(`Property: ${s['PROPERTY-ADDRESS']}`);
                  if (s['AUTOMOBILE-TYPE'])     parts.push(`Auto: ${s['AUTOMOBILE-TYPE']}`);
                  if (s['REGISTRATION-NUMBER']) parts.push(`Reg#: ${s['REGISTRATION-NUMBER']}`);
                  if (s['ENGINE-NUMBER'])       parts.push(`Engine#: ${s['ENGINE-NUMBER']}`);
                  if (s['CHASSIE-NUMBER'])      parts.push(`Chassis#: ${s['CHASSIE-NUMBER']}`);
                  return parts.filter(Boolean).join(' | ');
                }).filter(Boolean).join(' ; ') || '';
              })()],
              ['Linked Accounts',     (() => {
                const la = tl['LINKED-ACCOUNTS'];
                return Array.isArray(la) && la.length ? la.join(', ') : '';
              })()],
            ].filter(([,v]) => v && v !== '—');
            return (
              <div key={idx}>
                <div style={{backgroundColor: statusBg}} className="text-white text-[11px] font-bold px-3 py-1 mt-2">
                  {idx+1}. {acctType} — {lender} ({g(tl,'ACCOUNT-STATUS') || '?'})
                </div>
                <div className="px-2 py-1">
                  <div className="grid grid-cols-2 gap-x-4">
                    <div>{fields1.map(([l,v],i) => <FieldPair key={i} label={l} value={v} />)}</div>
                    <div>{fields2.map(([l,v],i) => <FieldPair key={i} label={l} value={v} />)}</div>
                  </div>
                  {histTypeNames.length > 0 && (
                    <div className="mt-2 space-y-3">
                      {histTypeNames.map(histType => {
                        const byYear = allHistory[histType];
                        const years  = Object.keys(byYear).sort().reverse();
                        if (!years.length) return null;
                        const label  = histType.replace(/-HISTORY$/,'').replace(/-/g,' ').trim() || 'History';
                        return (
                          <div key={histType}>
                            <p className="text-[10px] font-semibold text-[#00476F] mb-0.5">{label}</p>
                            <div className="overflow-x-auto">
                              <table className="text-[9px] border-collapse w-full">
                                <thead>
                                  <tr className="bg-[#c0d8ec]">
                                    <th className="border border-[#aac8df] px-1 py-0.5 font-bold w-10">Year</th>
                                    {MONTHS.map(m => (
                                      <th key={m} className="border border-[#aac8df] px-0.5 py-0.5 text-center font-semibold">{m}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {years.map(yr => (
                                    <tr key={yr} className="even:bg-[#f0f7ff]">
                                      <td className="border border-[#aac8df] px-1 py-0.5 font-bold text-center">{yr}</td>
                                      {MONTHS.map(m => (
                                        <td key={m} className="border border-[#aac8df] px-0.5 py-0.5 text-center text-[8px]">
                                          {byYear[yr][m] || ''}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ── Alerts (shown when data is present) ─────────── */}
      {(() => {
        const alerts = b2cReport?.['REPORT-DATA']?.['ALERTS'];
        const alertList = Array.isArray(alerts) ? alerts.filter(Boolean) : [];
        if (!alertList.length) return null;
        return (
          <>
            <SH title="ALERTS" />
            <div className="px-2 py-1 space-y-1">
              {alertList.map((alert, i) => (
                <div key={i} className="border border-red-200 bg-red-50 rounded px-3 py-1.5 text-[11px] text-red-800">
                  {typeof alert === 'string' ? alert : g(alert,'MESSAGE','DESCRIPTION','ALERT-TEXT','TEXT') || JSON.stringify(alert)}
                </div>
              ))}
            </div>
          </>
        );
      })()}

      {/* ── Inquiry History ───────────────────────────── */}
      {inquiries.length > 0 && (
        <>
          <SH title={`INQUIRY HISTORY (${inquiries.length} inquiries)`} />
          <div className="px-2 py-1 overflow-x-auto">
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="bg-[#d9e8f5]">
                  {['#','Lender Name','Lender Type','Inquiry Date','Ownership','Purpose','Amount','Loan Type','Remark'].map((h,i)=>(
                    <th key={i} className="border border-[#aac8df] px-1.5 py-0.5 text-center font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inquiries.map((inq, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f5f9ff]'}>
                    <td className="border border-[#aac8df] px-1.5 py-0.5 text-center">{i+1}</td>
                    <td className="border border-[#aac8df] px-1.5 py-0.5 font-medium">{g(inq,'LENDER-NAME','SUBSCRIBER-NAME','MEMBER-NAME')}</td>
                    <td className="border border-[#aac8df] px-1.5 py-0.5">{g(inq,'LENDER-TYPE','SUBSCRIBER-TYPE') || '—'}</td>
                    <td className="border border-[#aac8df] px-1.5 py-0.5 text-center">{g(inq,'INQUIRY-DT','INQUIRY-DATE')}</td>
                    <td className="border border-[#aac8df] px-1.5 py-0.5">{g(inq,'OWNERSHIP-TYPE') || '—'}</td>
                    <td className="border border-[#aac8df] px-1.5 py-0.5">{g(inq,'CREDIT-INQ-PURPS-TYPE','INQUIRY-PURPOSE','purpose')}</td>
                    <td className="border border-[#aac8df] px-1.5 py-0.5 text-right">{fmtAmt(g(inq,'AMOUNT','LOAN-AMT'))}</td>
                    <td className="border border-[#aac8df] px-1.5 py-0.5">{g(inq,'LOAN-TYPE') || '—'}</td>
                    <td className="border border-[#aac8df] px-1.5 py-0.5">{g(inq,'REMARK','remark') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Footer note (very small — visible when zoomed) ──── */}
      {pdfFooterNote && (() => {
        const note = (pdfFooterNote || '')
          .replace(/\{username\}/g, appPhone || '—')
          .replace(/\{fullname\}/g,  appName  || '—');
        return (
          <div className="mt-3 mx-3 pt-2 border-t border-gray-200 pb-3">
            <p style={{ fontSize: '7px', lineHeight: '1.3', color: '#9ca3af' }}>
              {note}
            </p>
          </div>
        );
      })()}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════ */
export default function CreditReportViewer({ consentText, pdfFooterNote, onReportResult }) {
  const { t, apiCall, user } = useApp();
  const [step, setStep]         = useState('kyc-form');
  const [consented, setConsented] = useState(false);
  const [aadhaarNum, setAadhaarNum] = useState('');
  const [mobileNo, setMobileNo]   = useState('');
  const [aadhaarData, setAadhaarData] = useState(null);
  const [kycProgress, setKycProgress] = useState(0);
  const [kycMsg, setKycMsg]     = useState('');
  const [form, setForm]         = useState({ first_name:'', last_name:'', mobile_no:'' });
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadMsg, setLoadMsg]   = useState('');
  const [report, setReport]     = useState(null);
  const [reportId, setReportId] = useState(null);
  const [error, setError]       = useState('');
  const [credits, setCredits]   = useState(user?.credit_balance || 0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const timerRef      = useRef(null);
  const kycTimerRef   = useRef(null);
  const isFetchingRef = useRef(false);
  const stepRef       = useRef('kyc-form');   // mirrors step state for cleanup closure
  const mobileNoRef   = useRef('');           // mirrors mobileNo for cleanup closure

  // Keep refs in sync
  React.useEffect(() => { stepRef.current = step; },    [step]);
  React.useEffect(() => { mobileNoRef.current = mobileNo; }, [mobileNo]);

  // ── Helper: silently log an event for Pull Analytics ────────
  const logEvent = (eventType, step, tabSwitchedTo = '', subjectMobile = '', buttonName = '') => {
    try {
      apiCall('POST', '/reports/log-event', {
        event_type:      eventType,
        button_name:     buttonName,
        step,
        tab_switched_to: tabSwitchedTo,
        subject_mobile:  subjectMobile,
        local_time:      new Date().toLocaleString(),
      }).catch(() => {});  // fire-and-forget, never block the UI
    } catch {}
  };

  // ── Cleanup all intervals on unmount ─────────────────────────
  React.useEffect(() => {
    return () => {
      if (timerRef.current)    clearInterval(timerRef.current);
      if (kycTimerRef.current) clearInterval(kycTimerRef.current);
      isFetchingRef.current = false;
      // Log tab switch on unmount if mid-flow (not on result/idle steps)
      if (!['kyc-form', 'result', 'low-credits', 'access-denied'].includes(stepRef.current)) {
        logEvent('tab_switch', stepRef.current, '', mobileNoRef.current);
      }
    };
  }, []);

  const kycMessages = [
    'Connecting to UIDAI gateway...',
    'Validating Aadhaar number...',
    'Fetching identity details...',
    'Verifying KYC information...',
    'Completing verification...',
  ];

  const loadMessages = ['Connecting to credit bureau...','Authenticating credentials...','Fetching credit history...','Analyzing accounts & score...','Preparing your report...'];

  // ── KYC: Verify Aadhaar ──────────────────────────────────────
  const handleVerifyAadhaar = async (e) => {
    e.preventDefault();
    setError('');
    if (!aadhaarNum || aadhaarNum.length < 12) { setError('Please enter a valid 12-digit Aadhaar number.'); return; }
    if (!mobileNo || mobileNo.length < 10)     { setError('Please enter a valid 10-digit mobile number.'); return; }

    // Clear any orphaned KYC timer from a previous attempt
    if (kycTimerRef.current) { clearInterval(kycTimerRef.current); kycTimerRef.current = null; }

    setStep('kyc-loading'); setKycProgress(0);
    let prog = 0;
    kycTimerRef.current = setInterval(() => {
      prog += 20;
      setKycProgress(prog);
      setKycMsg(kycMessages[Math.floor(prog / 20) - 1] || kycMessages[0]);
      if (prog >= 100) { clearInterval(kycTimerRef.current); kycTimerRef.current = null; doKycFetch(); }
    }, 600);
  };

  const doKycFetch = async () => {
    try {
      const data = await apiCall('POST', '/reports/fetch-aadhaar', { aadhaar: aadhaarNum, mobile_no: mobileNo });
      setAadhaarData(data);
      setStep('kyc-result');
    } catch (err) {
      setError(err.response?.data?.detail || 'Aadhaar verification failed. Please try again.');
      setStep('kyc-form');
    }
  };

  // ── Credit Report: pull using Aadhaar name + entered mobile ──
  const handleGetCreditReport = async (e) => {
    e?.preventDefault();
    if (!consented) { setError(t('consentRequired')); return; }
    setError('');

    // Smart name extraction — some Aadhaar API responses put the full name
    // in 'last_name' or 'fullname' with 'first_name' empty. Parse intelligently.
    let firstName = (aadhaarData?.first_name || '').trim();
    let lastName  = (aadhaarData?.last_name  || '').trim();
    const fullname = (aadhaarData?.fullname  || '').trim();

    if (!firstName) {
      // Use fullname if available, otherwise last_name may hold the full name
      const source = fullname || lastName;
      const parts  = source.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        firstName = parts.slice(0, -1).join(' ');
        lastName  = parts[parts.length - 1];
      } else {
        // Single-word name (e.g. "RISVANA") — CRIF API requires non-empty last_name
        firstName = source;
        lastName  = source;   // duplicate single-word name; IDS Pay accepts this
      }
    }

    // Ensure last_name is never empty — IDS Pay rejects empty last_name with 422
    if (!lastName) lastName = firstName;

    const reportForm = {
      first_name: firstName,
      last_name:  lastName,
      mobile_no:  mobileNo,
    };
    setForm(reportForm);

    // Check blocked numbers
    try {
      const resp = await apiCall('GET', '/developer/blocked-numbers');
      const blocked = resp.blocked_numbers || [];
      const isBlocked = blocked.some(b =>
        b.first_name.toLowerCase().trim() === reportForm.first_name.toLowerCase().trim() &&
        b.last_name.toLowerCase().trim()  === reportForm.last_name.toLowerCase().trim()  &&
        b.mobile_no.trim()                === reportForm.mobile_no.trim()
      );
      if (isBlocked) { setStep('access-denied'); return; }
    } catch {}

    setStep('loading'); setLoadProgress(0);

    // Log the button click for Pull Analytics
    logEvent('button_click', 'kyc-result', '', mobileNo, 'Get Credit Report');

    // Clear any orphaned loading timer + set the in-flight guard
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    isFetchingRef.current = true;

    let prog = 0;
    timerRef.current = setInterval(() => {
      prog += 20;
      setLoadProgress(prog);
      setLoadMsg(loadMessages[Math.floor(prog / 20) - 1] || loadMessages[0]);
      if (prog >= 100) {
        clearInterval(timerRef.current); timerRef.current = null;
        fetchReport(reportForm);
      }
    }, 1000);
  };

  const fetchReport = async (reportForm) => {
    // Guard: abort if component unmounted or another fetch already completed
    if (!isFetchingRef.current) return;
    isFetchingRef.current = false;  // consumed — prevents any duplicate interval firing

    const f = reportForm || form;
    try {
      const res = await apiCall('POST', '/reports/pull', f);
      setReport(res.report); setReportId(res.report_id);
      setCredits(res.credits_remaining); setStep('result');
      if (onReportResult) onReportResult('success');
    } catch (err) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      if (onReportResult) onReportResult('failure');
      if (err.response?.status === 402) setStep('low-credits');
      else { setError(err.response?.data?.detail || 'Failed to pull credit report'); setStep('kyc-result'); }
    }
  };

  // ── Download PDF: capture the HTML template exactly as shown ──
  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const element = document.getElementById('crif-report-template');
      if (!element) throw new Error('Report template not found');

      // Also expand the scrollable parent container so html2canvas captures full content
      const scrollParent = element.closest('.overflow-auto') || element.parentElement;
      const prevParentStyle = scrollParent?.style?.cssText || '';
      const prevStyle = element.style.cssText;

      if (scrollParent) {
        scrollParent.style.maxHeight = 'none';
        scrollParent.style.overflow  = 'visible';
        scrollParent.style.height    = 'auto';
      }
      element.style.width     = '860px';
      element.style.maxHeight = 'none';
      element.style.overflow  = 'visible';

      // Small delay to let layout re-calculate after style change
      await new Promise(r => setTimeout(r, 100));

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth:  element.scrollWidth,
        windowHeight: element.scrollHeight,
      });

      // Restore styles
      if (scrollParent) scrollParent.style.cssText = prevParentStyle;
      element.style.cssText = prevStyle;

      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdfW    = 210;                                     // A4 width mm
      const pdfH    = 297;                                     // A4 height mm
      const imgH    = (canvas.height * pdfW) / canvas.width;  // total image height mm

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      pdf.addImage(imgData, 'PNG', 0, 0, pdfW, imgH);

      // Split across pages if taller than one A4
      let heightLeft = imgH - pdfH;
      let yOffset    = -pdfH;
      while (heightLeft > 0) {
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, yOffset, pdfW, imgH);
        yOffset    -= pdfH;
        heightLeft -= pdfH;
      }

      const appSegment = report?.data?.result_json?.parsed_data?.['B2C-REPORT']?.['REQUEST-DATA']?.['APPLICANT-SEGMENT'] || {};
      const name = [appSegment['FIRST-NAME'], appSegment['LAST-NAME']].filter(Boolean).join('_').replace(/\s+/g, '_')
                   || `${form.first_name || 'Report'}_${form.last_name || ''}`;
      pdf.save(`CRIF_Report_${name}_${new Date().toISOString().split('T')[0]}.pdf`);

      if (reportId) { try { await apiCall('POST', `/reports/${reportId}/mark-downloaded`); } catch {} }
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  // Data extraction for display
  const parsedData  = report?.data?.result_json?.parsed_data || {};
  const b2cReport   = parsedData?.['B2C-REPORT'];
  const oldCr       = report?.data?.result_json?.credit_report || {};
  const usingB2C    = !!b2cReport;
  const b2cStd      = b2cReport?.['REPORT-DATA']?.['STANDARD-DATA'] || {};
  const b2cHeader   = b2cReport?.['HEADER-SEGMENT'] || {};
  const b2cScores   = b2cStd['SCORE'] || [];
  const firstScore  = extractScore(b2cScores, b2cReport, oldCr);
  const scoreValue  = firstScore?.value || 0;
  const displayData = usingB2C ? parsedData : (report?.data?.result_json || report);
  const isMock      = report?.mock;
  const tradeCount  = b2cStd['TRADELINES']?.length || 0;
  const inqCount    = b2cStd['INQUIRY-HISTORY']?.length || 0;

  /* ── KYC Form ──────────────────────────────────────────────── */
  if (step === 'kyc-form') return (
    <div className="max-w-lg" data-testid="kyc-form">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground">KYC &amp; Credit Report</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Enter Aadhaar and mobile number to verify identity, then fetch the credit report.
        </p>
      </div>
      <form onSubmit={handleVerifyAadhaar} className="space-y-4">
        <div>
          <label className="form-label">Aadhaar Number</label>
          <input
            data-testid="aadhaar-input"
            className="form-input tracking-[0.15em] text-center font-mono text-base"
            type="text"
            inputMode="numeric"
            maxLength={12}
            value={aadhaarNum}
            onChange={e => setAadhaarNum(e.target.value.replace(/\D/g, ''))}
            placeholder="XXXX XXXX XXXX"
            required
          />
        </div>
        <div>
          <label className="form-label">Mobile Number</label>
          <input
            data-testid="kyc-mobile-input"
            className="form-input"
            type="tel"
            maxLength={10}
            value={mobileNo}
            onChange={e => setMobileNo(e.target.value.replace(/\D/g, ''))}
            placeholder="10-digit mobile number"
            required
          />
        </div>
        {error && (
          <div className="px-3 py-2 bg-destructive/10 text-destructive text-sm rounded-lg border border-destructive/20">
            {error}
          </div>
        )}
        <button data-testid="verify-aadhaar-btn" type="submit"
          className="btn-primary w-full py-3 text-base">
          Verify Aadhaar &amp; Continue
        </button>
      </form>
    </div>
  );

  /* ── KYC Loading ─────────────────────────────────────────────── */
  if (step === 'kyc-loading') return (
    <div className="max-w-sm mx-auto text-center py-12" data-testid="kyc-loading">
      <div className="relative w-24 h-24 mx-auto mb-6">
        <div className="absolute inset-0 rounded-full border-4 border-emerald-200 animate-spin border-t-emerald-500" />
        <div className="absolute inset-3 rounded-full border-2 border-emerald-100 animate-ping" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-bold text-emerald-600">{kycProgress}%</span>
        </div>
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-1">Verifying Aadhaar Identity</h3>
      <p className="text-sm text-muted-foreground mb-4">{kycMsg}</p>
      <div className="w-full bg-secondary rounded-full h-1.5">
        <div className="bg-emerald-500 h-1.5 rounded-full transition-all duration-500" style={{ width: `${kycProgress}%` }} />
      </div>
    </div>
  );

  /* ── KYC Result ──────────────────────────────────────────────── */
  if (step === 'kyc-result') return (
    <div className="max-w-xl animate-fade-in" data-testid="kyc-result">
      {/* Success banner */}
      <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-300 rounded-xl mb-5 blink-success">
        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <span className="text-white text-sm font-bold">✓</span>
        </div>
        <div>
          <p className="font-semibold text-emerald-800 text-sm">Aadhaar KYC Verification Completed</p>
          <p className="text-xs text-emerald-600 mt-0.5">Identity confirmed via UIDAI</p>
        </div>
      </div>

      {/* Aadhaar data table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden mb-5">
        <div className="bg-[#00476F] text-white text-xs font-bold px-4 py-2">
          Aadhaar Identity Details
        </div>
        <table className="w-full text-sm">
          <tbody>
            {[
              ['Full Name',    aadhaarData?.fullname],
              ['First Name',  aadhaarData?.first_name],
              ['Middle Name', aadhaarData?.middle_name],
              ['Last Name',   aadhaarData?.last_name],
              ['Gender',      aadhaarData?.gender],
              ['Date of Birth', aadhaarData?.dob],
              ['Aadhaar Number', aadhaarData?.aadhaar_number ? `XXXX XXXX ${String(aadhaarData.aadhaar_number).slice(-4)}` : '—'],
              ['PAN',         aadhaarData?.pan],
              ['City',        aadhaarData?.city],
              ['State',       aadhaarData?.state],
              ['Country',     aadhaarData?.country],
            ].filter(([,v]) => v && v.trim && v.trim()).map(([label, val], i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f5f9ff]'}>
                <td className="border border-[#e5eaf0] px-4 py-2 font-semibold text-[#00476F] w-40">{label}</td>
                <td className="border border-[#e5eaf0] px-4 py-2 text-gray-800">{val}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Consent + Get Credit Report */}
      <div className="space-y-4">
        <div className="p-4 bg-secondary/50 rounded-xl border border-border">
          <label className="flex gap-3 cursor-pointer">
            <input data-testid="consent-checkbox" type="checkbox" checked={consented}
              onChange={e => setConsented(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded accent-primary" />
            <span className="text-sm leading-relaxed">
              {consentText || 'I hereby consent to the access and processing of credit report information in accordance with applicable laws and regulations.'}
            </span>
          </label>
        </div>
        {error && (
          <div className="px-3 py-2 bg-destructive/10 text-destructive text-sm rounded-lg border border-destructive/20">
            {error}
          </div>
        )}
        <div className="flex gap-3">
          <button data-testid="get-credit-report-btn" onClick={handleGetCreditReport}
            disabled={!consented}
            className={`btn-primary flex-1 py-3 text-base ${!consented ? 'opacity-40 cursor-not-allowed' : ''}`}>
            Get Credit Report
          </button>
          <button onClick={() => { setStep('kyc-form'); setConsented(false); setError(''); setAadhaarData(null); }}
            className="btn-secondary px-6">
            Back
          </button>
        </div>
      </div>
    </div>
  );

  /* ── Access Denied ─────────────────────────────────────────────── */
  if (step === 'access-denied') return (
    <div data-testid="access-denied-popup" className="max-w-md mx-auto text-center py-12">
      <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
        <ShieldOff size={28} className="text-red-600" />
      </div>
      <h3 className="text-xl font-bold text-foreground mb-2">Access Denied</h3>
      <p className="text-muted-foreground mb-6">
        This name and mobile number combination has been restricted.<br />
        Please contact your administrator.
      </p>
      <button onClick={() => { setStep('kyc-form'); setError(''); }} className="btn-secondary">
        Back to Form
      </button>
    </div>
  );

  /* ── Low credits ─────────────────────────────────────────────── */
  if (step === 'low-credits') return (
    <div data-testid="low-credits-popup" className="max-w-md mx-auto text-center py-12">
      <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
        <AlertCircle size={28} className="text-destructive" />
      </div>
      <h3 className="text-xl font-bold mb-2">{t('lowCreditsTitle')}</h3>
      <p className="text-muted-foreground mb-6">{t('lowCreditsMsg')}</p>
      <button onClick={() => setStep('kyc-form')} className="btn-secondary mr-3">{t('back')}</button>
      {user?.role === 'admin' && <button className="btn-primary">{t('purchaseMore')}</button>}
      {user?.role === 'ngo'   && <p className="text-sm text-muted-foreground mt-3">{t('contactAdmin')}</p>}
    </div>
  );

  /* ── Loading ── */
  if (step === 'loading') return (
    <div className="max-w-sm mx-auto text-center py-12" data-testid="report-loading">
      <div className="relative w-24 h-24 mx-auto mb-6">
        <div className="absolute inset-0 rounded-full border-4 border-primary/20 animate-spin border-t-primary" />
        <div className="absolute inset-3 rounded-full border-2 border-primary/10 animate-ping" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-bold text-primary">{loadProgress}%</span>
        </div>
      </div>
      <h3 className="text-lg font-semibold mb-1">{t('loadingReport')}</h3>
      <p className="text-sm text-muted-foreground mb-4">{loadMsg}</p>
      <div className="w-full bg-secondary rounded-full h-1.5">
        <div className="bg-primary h-1.5 rounded-full transition-all duration-1000" style={{ width: `${loadProgress}%` }} />
      </div>
    </div>
  );

  /* ── Result ── */
  if (step === 'result') return (
    <div className="space-y-3 animate-fade-in" data-testid="credit-report-result">
      {/* Action bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-bold">{t('creditReport')}</h3>
          <p className="text-xs text-muted-foreground">
            Credits remaining: <strong>{credits}</strong>
            {b2cHeader?.['REPORT-ID'] && <span className="ml-3">ID: {b2cHeader['REPORT-ID']}</span>}
            {b2cHeader?.['DATE-OF-ISSUE'] && <span className="ml-3">Date: {b2cHeader['DATE-OF-ISSUE']}</span>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="download-report-btn" onClick={handleDownloadPDF} disabled={pdfLoading}
            className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-60">
            <Download size={13} />{pdfLoading ? 'Generating PDF…' : 'Download PDF'}
          </button>
          <button data-testid="pull-new-report-btn"
            onClick={() => { setStep('kyc-form'); setConsented(false); setReport(null); setAadhaarData(null); setAadhaarNum(''); setMobileNo(''); setError(''); }}
            className="btn-secondary text-xs flex items-center gap-1.5">
            <RefreshCw size={13} />{t('pullNewReport')}
          </button>
        </div>
      </div>

      {/* Status badges */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${isMock ? 'bg-amber-500/10 text-amber-600' : 'bg-emerald-500/10 text-emerald-600'}`}>
          {isMock ? '⚠ Demo Mode' : '✓ Live Report'}
        </span>
        {usingB2C && (
          <span className="text-xs text-muted-foreground">
            {tradeCount} account{tradeCount !== 1 ? 's' : ''} · {inqCount} inquir{inqCount !== 1 ? 'ies' : 'y'}
          </span>
        )}
      </div>

      {/* Report Template */}
      <div className="border border-border rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-auto max-h-[75vh] bg-white">
          <CreditReportTemplate b2cReport={b2cReport} oldCr={oldCr} isMock={isMock} pdfFooterNote={pdfFooterNote} user={user} />
        </div>
      </div>
    </div>
  );

  /* ── Fallback: redirect to KYC form ── */
  return (
    <div className="max-w-lg" data-testid="credit-report-form">
      <div className="mb-6">
        <h3 className="text-lg font-semibold">{t('creditReport')}</h3>
        <p className="text-sm text-muted-foreground mt-1">Credits available: <strong>{credits}</strong></p>
      </div>
      <div className="text-center py-8">
        <p className="text-muted-foreground mb-4">Please complete Aadhaar KYC to proceed.</p>
        <button onClick={() => setStep('kyc-form')} className="btn-primary">
          Start KYC Verification
        </button>
      </div>
    </div>
  );
}
